import { useAnnouncementBackgroundImage } from '@components/announcement/hooks/useAnnouncementBackgroundImage.hooks';
import { type AnnouncementItemEditorState } from '@components/announcement/util/announcementItemEditorState.util';
import { useCommonModalConfig } from '@hclinear/hc-linear-react';

interface UseAnnouncementBackgroundImageEditorProps {
  editorState: AnnouncementItemEditorState;
  updateEditorState: (patch: Partial<AnnouncementItemEditorState>) => void;
}

interface UseAnnouncementBackgroundImageEditorResult {
  backgroundImage: string | undefined;
  backgroundImageName: string | undefined;
  imageGalleryModalOpen: boolean;
  onSelectBackgroundImage: () => void;
  onImageGalleryModalClose: () => void;
  onUploadBackgroundImage: (base64File: string, fileName: string) => void;
  onGallerySubmit: (imageId: number) => void;
  onRemoveBackgroundImage: () => void;
}

export const useAnnouncementBackgroundImageEditor = ({
  editorState,
  updateEditorState,
}: UseAnnouncementBackgroundImageEditorProps): UseAnnouncementBackgroundImageEditorResult => {
  const { backgroundImage, backgroundImageName } = useAnnouncementBackgroundImage({
    imageId: editorState.imageId,
    file: editorState.file,
    fileName: editorState.fileName,
  });

  const {
    modalConfig: imageGalleryModalConfig,
    onModalOpen: onSelectBackgroundImage,
    onModalClose: onImageGalleryModalClose,
  } = useCommonModalConfig();

  const onUploadBackgroundImage = (base64File: string, uploadedFileName: string) => {
    updateEditorState({
      file: base64File,
      fileName: uploadedFileName,
      imageId: undefined,
    });
  };

  const onGallerySubmit = (selectedImageId: number) => {
    updateEditorState({ imageId: selectedImageId, file: undefined, fileName: undefined });
    onImageGalleryModalClose();
  };

  const onRemoveBackgroundImage = () => {
    updateEditorState({ imageId: undefined, file: undefined, fileName: undefined });
  };

  return {
    backgroundImage,
    backgroundImageName,
    imageGalleryModalOpen: imageGalleryModalConfig.open,
    onSelectBackgroundImage,
    onImageGalleryModalClose,
    onUploadBackgroundImage,
    onGallerySubmit,
    onRemoveBackgroundImage,
  };
};
